import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import DateField from "@/components/ui/date-field";
import DriverSelect from "@/components/ui/driver-select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { SubmitButton } from "@/components/ui/submit-button";
import { Textarea } from "@/components/ui/textarea";
import { errorHandler } from "@/lib/utils";
import { driverLeaveService } from "@/services/api/driverLeaves";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import * as z from "zod";

const formSchema = z
  .object({
    driver_id: z.number({
      error: "Por favor, selecione um motorista",
    }),
    start_date: z.string().date("Data inválida"),
    end_date: z.string().date("Data inválida"),
    reason: z.string({
      error: "Por favor, informe o motivo da licença/atestado",
    }),
  })
  .refine(
    (data) => {
      const startDate = new Date(data.start_date);
      const endDate = new Date(data.end_date);
      return endDate >= startDate;
    },
    {
      message: "A data de fim deve ser igual ou posterior à data de início",
      path: ["end_date"],
    },
  );

export type DriverLeaveFormValues = z.infer<typeof formSchema>;

const DriverLeavesForm = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const isEditing = !!id;

  const form = useForm<DriverLeaveFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      driver_id: undefined as unknown as number,
      start_date: "",
      end_date: "",
      reason: "",
    },
  });

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      driverLeaveService
        .show(Number(id))
        .then((response) => {
          const leave = response.data;
          form.setValue("driver_id", leave.driver_id);
          form.setValue("start_date", leave.start_date);
          form.setValue("end_date", leave.end_date);
          form.setValue("reason", leave.reason);
        })
        .catch((err) => {
          errorHandler(err, "Erro ao carregar dados da licença/atestado.");
        })
        .finally(() => setIsLoading(false));
    }
  }, [id, form]);

  const onSubmit = async (values: DriverLeaveFormValues) => {
    setIsLoading(true);
    try {
      if (isEditing) {
        await driverLeaveService.update(Number(id), values);
        toast.success("Licença/Atestado atualizado com sucesso!");
      } else {
        await driverLeaveService.create(values);
        toast.success("Licença/Atestado cadastrado com sucesso!");
      }
      navigate("/driver-leaves");
    } catch (err) {
      errorHandler(err, "Erro ao salvar licença/atestado.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/driver-leaves");
  };

  return (
    <Card className="mx-auto">
      <CardHeader>
        <CardTitle>
          {isEditing ? "Editar Licença/atestado" : "Cadastrar Licença/atestado"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="driver_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Motorista</FormLabel>
                  <FormControl>
                    <DriverSelect
                      value={field.value}
                      onChange={(value) => field.onChange(Number(value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DateField form={form} name="start_date" label="Início" />

            <DateField form={form} name="end_date" label="Fim" />

            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Motivo</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Informe o motivo da licença/atestado"
                      className="min-h-[120px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col space-y-2 pt-4 border-t sm:flex-row sm:space-x-2 sm:space-y-0 justify-end">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancelar
              </Button>
              <SubmitButton loading={isLoading} />
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default DriverLeavesForm;
